/**
 * VoiceHint — Contextual voice trigger button.
 *
 * Phase 7: Small mic icon button placed near features to provide
 * contextual voice assistance. In v1, shows a "Coming Soon" tooltip.
 * In v2, will activate the voice agent with the given context hint.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic } from "lucide-react";
import { useVoiceAgent } from "@/contexts/VoiceAgentContext";

export default function VoiceHint({ context = "general", label = "Ask voice assistant" }) {
  const { isAvailable, triggerWithContext } = useVoiceAgent();
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-flex" data-testid={`voice-hint-${context}`}>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => {
          if (isAvailable) {
            triggerWithContext(context);
          }
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--qn-surface)] border border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40 hover:bg-[var(--qn-primary)]/10 transition-all"
        aria-label={label}
      >
        <Mic className="w-3.5 h-3.5 text-[var(--qn-foreground-subtle)]" />
      </motion.button>

      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap px-2.5 py-1.5 rounded-lg bg-[var(--qn-card)] border border-[var(--qn-border)] shadow-lg text-xs text-[var(--qn-foreground-muted)] z-50"
          >
            {isAvailable ? label : "Voice Guide — Coming Soon"}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
